import React, { useEffect, useRef, useState } from "react";
import { forwardRef } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { stackData } from "../data/skillsData";
import '../styles/skills.css';

/* ------------------------------------------------------------------ */
/*  Inline SVG icons — one per capability card                        */
/* ------------------------------------------------------------------ */

const FrontendIcon = () => (
  <svg
    className="skills-cap__card-icon"
    viewBox="0 0 40 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    {/* Browser chrome */}
    <rect x="3" y="5" width="34" height="28" rx="4" stroke="currentColor" strokeWidth="1.6" />
    <line x1="3" y1="12" x2="37" y2="12" stroke="currentColor" strokeWidth="1.2" opacity="0.5" />
    <circle cx="7.5" cy="8.5" r="1" fill="currentColor" opacity="0.45" />
    <circle cx="11" cy="8.5" r="1" fill="currentColor" opacity="0.45" />
    <circle cx="14.5" cy="8.5" r="1" fill="currentColor" opacity="0.45" />
    {/* Layout blocks */}
    <rect x="6" y="15" width="12" height="14" rx="2" stroke="currentColor" strokeWidth="1.2" opacity="0.7" />
    <rect x="21" y="15" width="13" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.2" opacity="0.7" />
    <rect x="21" y="23.5" width="13" height="5.5" rx="1.5" stroke="currentColor" strokeWidth="1.2" opacity="0.7" />
  </svg>
);

const BackendIcon = () => (
  <svg
    className="skills-cap__card-icon"
    viewBox="0 0 40 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    {/* Terminal / server box */}
    <rect x="4" y="6" width="32" height="12" rx="3" stroke="currentColor" strokeWidth="1.6" />
    <line x1="9" y1="10" x2="14" y2="10" stroke="currentColor" strokeWidth="1.3" opacity="0.6" />
    <line x1="9" y1="14" x2="18" y2="14" stroke="currentColor" strokeWidth="1.3" opacity="0.4" />
    <circle cx="31" cy="12" r="1.5" fill="currentColor" opacity="0.5" />
    {/* Data flow lines */}
    <line x1="14" y1="18" x2="14" y2="24" stroke="currentColor" strokeWidth="1.2" opacity="0.4" />
    <line x1="20" y1="18" x2="20" y2="22" stroke="currentColor" strokeWidth="1.2" opacity="0.4" />
    <line x1="26" y1="18" x2="26" y2="24" stroke="currentColor" strokeWidth="1.2" opacity="0.4" />
    {/* Database cylinder */}
    <ellipse cx="20" cy="28" rx="10" ry="3" stroke="currentColor" strokeWidth="1.4" opacity="0.65" />
    <line x1="10" y1="28" x2="10" y2="33" stroke="currentColor" strokeWidth="1.4" opacity="0.65" />
    <line x1="30" y1="28" x2="30" y2="33" stroke="currentColor" strokeWidth="1.4" opacity="0.65" />
    <ellipse cx="20" cy="33" rx="10" ry="3" stroke="currentColor" strokeWidth="1.4" opacity="0.65" />
  </svg>
);

const CustomBuildsIcon = () => (
  <svg
    className="skills-cap__card-icon"
    viewBox="0 0 40 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    {/* Wrench */}
    <path
      d="M10 30 L18 22 C16.5 19 17 15.5 19.5 13 C22 10.5 25.5 10 28.5 11.5 L24 16 L24.5 19.5 L28 20 L32.5 15.5 C33.5 18.5 33 22 30.5 24.5 C28 27 24.5 27.5 21.5 26 L13.5 34 C12.5 35 11 35 10 34 C9 33 9 31.5 10 30Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
      opacity="0.75"
    />
    {/* Code bracket accent */}
    <path d="M6 14 L2 18 L6 22" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" opacity="0.4" />
    <path d="M14 6 L18 2 L14 -2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" opacity="0" />
  </svg>
);

const cardIcons = [FrontendIcon, BackendIcon, CustomBuildsIcon];

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

const capabilityCards = [
  {
    title: "Frontend Systems",
    text: "Responsive, scalable interfaces built with React, modern CSS, component thinking, and performance-first implementation.",
    tags: ["Responsive UI", "Component Architecture", "Performance-first", "Modern CSS"],
  },
  {
    title: "Backend & APIs",
    text: "Structured backend logic, API integrations, data handling, and maintainable service architecture built for real-world products.",
    tags: ["REST APIs", "Data Handling", "Node.js Services", "Database Design"],
  },
  {
    title: "WordPress & Custom Builds",
    text: "Custom themes, advanced CMS workflows, and flexible marketing sites built without sacrificing code quality or performance.",
    tags: ["Custom Themes", "CMS Workflows", "Marketing Sites", "Code Quality"],
  },
];

const techRows = [
  stackData.slice(0, 5),
  stackData.slice(5, 10),
  stackData.slice(10),
];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export const Skills = forwardRef((_props, skillsSection) => {
  const [isVisible, setIsVisible] = useState(false);
  const revealRef = useRef(null);

  useEffect(() => {
    const el = revealRef.current;
    if (!el || !("IntersectionObserver" in window)) {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "0px 0px -8% 0px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="skills" className="skills-cap" ref={skillsSection}>
      <div className="skills-cap__bg" aria-hidden="true" />

      <Container fluid className="skills-cap__fluid px-lg-0 px-3">
        <Row className="justify-content-center skills-cap__row">
          <Col lg={10} className="skills-cap__col">
            <div className="skills-cap__inner" ref={revealRef}>

              {/* Intro */}
              <div className={`skills-cap__intro${isVisible ? " is-visible" : ""}`}>
                <p className="skills-cap__eyebrow">CAPABILITIES</p>
                <h2 className="skills-cap__heading">Built for modern web products</h2>
                <p className="skills-cap__subhead">
                  I design and develop fast, maintainable, production-ready systems
                  across frontend, backend, and custom web platforms.
                </p>
              </div>

              {/* Capability cards */}
              <div className={`skills-cap__cards${isVisible ? " is-visible" : ""}`}>
                {capabilityCards.map((card, i) => {
                  const IconComponent = cardIcons[i];
                  return (
                    <div
                      className={
                        i === 0
                          ? "skills-cap__card skills-cap__card--featured"
                          : "skills-cap__card"
                      }
                      key={card.title}
                      style={{ "--card-index": i }}
                    >
                      <div className="skills-cap__card-icon-wrap">
                        <IconComponent />
                      </div>
                      <h3 className="skills-cap__card-title">{card.title}</h3>
                      <p className="skills-cap__card-text">{card.text}</p>
                      <ul
                        className="skills-cap__card-tags"
                        aria-label={`${card.title} capabilities`}
                      >
                        {card.tags.map((tag) => (
                          <li key={tag} className="skills-cap__card-tag">{tag}</li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>

              {/* Stack showcase */}
              <div className={`skills-cap__stack${isVisible ? " is-visible" : ""}`}>
                <div className="skills-cap__stack-header">
                  <p className="skills-cap__stack-label">Core stack</p>
                  <h3 className="skills-cap__stack-heading">The technologies I build with</h3>
                </div>

                <div
                  className="skills-cap__tech-grid"
                  role="list"
                  aria-label="Core technologies"
                >
                  {techRows.map((row, rowIndex) => (
                    <div
                      className={`skills-cap__tech-row skills-cap__tech-row--${row.length}`}
                      key={`tech-row-${rowIndex}`}
                      role="presentation"
                    >
                      {row.map((tech) => (
                        <div className="skills-cap__tech-item" key={tech.name} role="listitem">
                          <img
                            src={tech.logo}
                            alt={tech.name}
                            className="skills-cap__tech-logo"
                            width="48"
                            height="48"
                            loading="lazy"
                            decoding="async"
                          />
                          <span className="skills-cap__tech-name">{tech.name}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
});